import calendar from '@/assets/calendar.svg'
import {
    getLocalTimeZone,
    parseDate,
    today,
    ZonedDateTime,
} from '@internationalized/date'
import { DateRangePicker, DateValue, RangeValue } from '@nextui-org/react'
import { I18nProvider } from '@react-aria/i18n'
import React, { FC, useEffect, useRef, useState } from 'react'
import { DateRangeType } from '../survey/SurveyModel/types/commonTypes'

//import 'react-datepicker/dist/react-datepicker.css';
//import InputMask from "react-input-mask";

interface IdateRange {
    name: string
    onChange: (update: DateRangeType | null, name: string) => void
    initialDate?: DateRangeType
    propClasses: string
    isPatient: boolean
    disabled?: boolean
    propDivClasses?: string
    isInValid?: boolean
    onBlur?: () => void
    calendarWidth?: number
    defaultCalendarOpen?: boolean
}

const DateRange: FC<IdateRange> = (props: IdateRange) => {
    const {
        name,
        onChange,
        initialDate,
        isPatient = false,
        disabled = false,
        isInValid,
        propDivClasses = '',
        onBlur,
        calendarWidth = 308,
        defaultCalendarOpen = false,
    } = props
    const [date, setDate] = React.useState<RangeValue<DateValue> | null>()
    const isInitialRun = useRef(true)
    const [isFocused, setIsFocused] = useState(false)
    const [isActive, setIsActive] = useState(false)
    const [isOpen, setIsOpen] = useState(defaultCalendarOpen)

    useEffect(() => {
        if (isInitialRun.current && initialDate) {
            isInitialRun.current = false
            const start = initialDate.start
            const end = initialDate.end
            if (start && end) {
                const newDate = {
                    start: parseDate(start as unknown as string), // Explicitly cast to string
                    end: parseDate(end as unknown as string),
                }

                setDate(newDate)
            }
        }
    }, [initialDate])
    // const createDate = (newDate: any) => {
    //   if(newDate  && newDate.year && newDate.month && newDate.day){
    //     const calendarDate = new CalendarDate(newDate.year, newDate.month, newDate.day);

    //     // Format to dd/mm/yyyy
    //     const formattedDate = `${calendarDate.year}-${String(calendarDate.month).padStart(2, '0')}-${String(calendarDate.day).padStart(2, '0')}`;
    //     return formattedDate
    //   }
    //   return ''

    // }

    const formatUKDate = (date: ZonedDateTime) => {
        const day = String(date.day).padStart(2, '0')
        const month = String(date.month).padStart(2, '0')
        const year = date.year
        return `${day}-${month}-${year}`
    }

    const handleDateChange = (newDate: RangeValue<DateValue> | null) => {
        if (!newDate) return
        setDate(newDate)
        isInitialRun.current = false
        if (newDate) {
            const startDate = newDate.start
                ? formatUKDate(newDate.start as ZonedDateTime)
                : null
            const endDate = newDate.end
                ? formatUKDate(newDate.end as ZonedDateTime)
                : null

            if (startDate && endDate) {
                onChange({ start: startDate, end: endDate }, name)
                // Close the calendar once the full range is selected.
                // This prevents the popover overlay from lingering after the parent filter closes.
                setIsOpen(false)
                setIsFocused(false)
                setIsActive(false)
            }
        }
    }

    const handleOnBlure = () => {
        if (onBlur) onBlur()
        setIsFocused(false)
        setIsActive(false)
    }

    const clearDateRange = () => {
        setDate(null)
        onChange(null, name)
        setIsOpen(false)
    }
    const closeCalendar = () => setIsOpen(false)

    return (
        <div className="date-range-picker flex-1">
            <div className={`${isInValid ? 'error' : ''}`}>
                <div
                    className={`${propDivClasses} input-div ${isFocused ? 'input-div-focus' : ''} ${isActive ? 'input-div-active' : ''} ${disabled ? 'disabled' : ''}`}
                >
                    <I18nProvider locale="en-GB">
                        <DateRangePicker
                            disableAnimation
                            className={`custom-font-family customize-daterange max-h-[36px] ${isPatient ? '' : ' customize-daterange-poa'} ${disabled ? 'disabled' : ''}`}
                            classNames={{
                                popoverContent:
                                    '-translate-y-[5px] border-0 ring-0 outline-none !shadow-[0_4px_8px_0_rgba(63,63,63,0.14),4px_6px_10px_-6px_rgba(63,63,63,0.12),-4px_6px_10px_-6px_rgba(63,63,63,0.12)]',
                            }}
                            popoverProps={{
                                className:
                                    'before:shadow-none shadow-none ring-0 border-0',
                            }}
                            onChange={handleDateChange}
                            onBlur={handleOnBlure}
                            value={date}
                            calendarWidth={calendarWidth}
                            isOpen={isOpen}
                            onFocus={() => setIsFocused(true)}
                            onOpenChange={setIsOpen}
                            isDisabled={disabled}
                            minValue={
                                isPatient
                                    ? today(getLocalTimeZone())
                                    : parseDate('1900-04-08')
                            }
                            errorMessage={(value) => {
                                if (value.isInvalid) {
                                    return ''
                                }
                            }}
                            selectorIcon={
                                <span className="custom-icon">
                                    <img
                                        src={calendar}
                                        alt="Calendar"
                                        className="w-4 h-4"
                                    />
                                </span>
                            }
                            CalendarBottomContent={
                                <div>
                                    <div className="flex justify-between calendar-bottom">
                                        <button
                                            className="clear"
                                            onMouseDown={(e) =>
                                                e.stopPropagation()
                                            }
                                            onClick={() => clearDateRange()}
                                        >
                                            Clear
                                        </button>
                                        <button
                                            className="OK"
                                            onMouseDown={(e) =>
                                                e.stopPropagation()
                                            }
                                            onClick={() => closeCalendar()}
                                        >
                                            OK
                                        </button>
                                    </div>
                                </div>
                            }
                            calendarProps={{
                                classNames: {
                                    base: 'bg-background custom-font-family shadow-none border-0 ring-0',
                                    headerWrapper:
                                        'pt-4 bg-background custom-font-family',
                                    prevButton:
                                        'border-1 border-default-200 rounded-small custom-header-color  font-custom',
                                    nextButton:
                                        'border-1 border-default-200 rounded-small custom-header-color font-custom',
                                    gridHeader:
                                        'bg-background !shadow-none border-b-0 custom-header-color font-custom custom-font-family',
                                    cellButton: [
                                        'data-[today=true]:bg-default-100 data-[selected=true]:bg-transparent rounded-small',
                                        'data-[range-start=true]:before:rounded-l-small',
                                        'data-[selection-start=true]:before:rounded-l-small',
                                        'data-[range-end=true]:before:rounded-r-small',
                                        'data-[selection-end=true]:before:rounded-r-small',
                                        'data-[selected=true]:data-[selection-start=true]:data-[range-selection=true]:rounded-small',
                                        'data-[selected=true]:data-[selection-end=true]:data-[range-selection=true]:rounded-small',
                                    ],
                                },
                            }}
                        />
                    </I18nProvider>
                </div>
            </div>
        </div>
    )
}

export default DateRange
